import type { PadSnapshot, PerformancePadId } from "./types";

interface Pulse {
  startedAt: number;
  durationMs: number;
}

/** Stateful pad behavior kept outside React so held notes survive UI rerenders. */
export class PerformancePads {
  #held = new Set<PerformancePadId>();
  #pulses = new Map<PerformancePadId, Pulse>();
  #panic = false;
  #safeOutput = true;

  get safeOutput(): boolean {
    return this.#safeOutput;
  }

  setSafeOutput(enabled: boolean, warningAcknowledged = false): void {
    if (!enabled && !warningAcknowledged) {
      throw new Error("Disabling Safe Output requires an explicit photosensitivity warning acknowledgement");
    }
    this.#safeOutput = enabled;
  }

  press(id: PerformancePadId, now = performance.now()): void {
    if (id === "panic") {
      this.#panic = true;
      this.#held.clear();
      this.#pulses.clear();
      return;
    }
    if (this.#panic) return;
    if (id === "syncKick") this.#pulses.set(id, { startedAt: now, durationMs: 120 });
    else if (id === "lineCrush") this.#pulses.set(id, { startedAt: now, durationMs: 96 });
    else if (id === "frameTear") this.#pulses.set(id, { startedAt: now, durationMs: 180 });
    else this.#held.add(id);
  }

  release(id: PerformancePadId): void {
    this.#held.delete(id);
  }

  resume(): void {
    this.#panic = false;
  }

  reset(): void {
    this.#held.clear();
    this.#pulses.clear();
  }

  snapshot(now = performance.now()): PadSnapshot {
    const pulse = (id: PerformancePadId): number => {
      const item = this.#pulses.get(id);
      if (!item) return 0;
      const progress = (now - item.startedAt) / item.durationMs;
      if (progress >= 1) {
        this.#pulses.delete(id);
        return 0;
      }
      return Math.exp(-5 * Math.max(0, progress));
    };

    const whiteout = this.#held.has("whiteout") ? (this.#safeOutput ? 0.62 : 1) : 0;
    const unlock = this.#held.has("unlock") ? (this.#safeOutput ? 0.55 : 1) : 0;
    return {
      syncKick: pulse("syncKick"),
      burstKill: this.#held.has("burstKill") ? 1 : 0,
      lineCrush: pulse("lineCrush"),
      frameTear: pulse("frameTear"),
      whiteout,
      freeze: this.#held.has("freeze"),
      unlock,
      panic: this.#panic,
    };
  }
}
